import {
  createIntlCache,
  createIntl as createIntlFormatjs,
  IntlErrorCode,
  IntlShape,
} from "@formatjs/intl";

import deShared from "ps-design-system/intl-shared-component/compiled/de-DE.json";
import gbShared from "ps-design-system/intl-shared-component/compiled/en-GB.json";
import enShared from "ps-design-system/intl-shared-component/compiled/en-US.json";
import esShared from "ps-design-system/intl-shared-component/compiled/es-ES.json";
import frShared from "ps-design-system/intl-shared-component/compiled/fr-FR.json";

import deJson from "intl-web-listings/compiled/de-DE.json";
import gbJson from "intl-web-listings/compiled/en-GB.json";
import enJson from "intl-web-listings/compiled/en-US.json";
import esJson from "intl-web-listings/compiled/es-ES.json";
import frJson from "intl-web-listings/compiled/fr-FR.json";

/**
 * Loads messages for a given locale from the compiled JSON files.
 *
 * @param translationLocale - The locale string to load translations for.
 * @returns Record<string, string> - The translated messages for the given locale.
 */
const loadTranslationData = (
  translationLocale: string,
): Record<string, string> => {
  const lowercaseLocale = translationLocale.toLowerCase();
  switch (lowercaseLocale) {
    case "fr-fr":
    case "fr":
      return { ...frJson, ...frShared };
    case "en-gb":
    case "gb":
      return { ...gbJson, ...gbShared };
    case "de-de":
    case "de":
      return { ...deJson, ...deShared };
    case "es-es":
    case "es":
      return { ...esJson, ...esShared };
    case "en-us":
    case "en":
    default:
      return { ...enJson, ...enShared };
  }
};

type IntlErrorHandler = NonNullable<
  Parameters<typeof createIntlFormatjs>[0]["onError"]
>;

/**
 * Swallows the noisy "Missing message: ..." / "Missing locale data ..." warnings
 * react-intl logs for every untranslated id. Those ids fall back to their
 * defaultMessage, so they are expected rather than actionable — everything else
 * still gets logged.
 *
 * @param error - The error react-intl encountered while formatting.
 */
const handleIntlError: IntlErrorHandler = (error) => {
  if (
    error.code === IntlErrorCode.MISSING_TRANSLATION ||
    error.code === IntlErrorCode.MISSING_DATA
  ) {
    return;
  }

  console.error(error);
};

const intlCache = createIntlCache();

/**
 * Creates an intl object for the given locale. Useful when you need to use the intl object outside of React, e.g. in a page's generateMetadata function.
 * @param locale
 * @returns IntlShape
 */
const createIntl = (locale: string): IntlShape => {
  const messages = loadTranslationData(locale);

  return createIntlFormatjs(
    {
      locale,
      messages,
      defaultLocale: "en-US",
      onError: handleIntlError,
    },
    intlCache,
  );
};

export { createIntl, handleIntlError, loadTranslationData };
